import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { calcularEdadMeses } from '../../utils/helpers';
import { buscarAnimalOffline, submitOffline } from '../../services/offlineHelpers';

const RegistrarControlMachoForm = ({ onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const [macho, setMacho] = useState(null);
  const [buscarIDE, setBuscarIDE] = useState('');
  const [buscandoMacho, setBuscandoMacho] = useState(false);

  const [formData, setFormData] = useState({
    macho_id: '', fecha: new Date().toISOString().split('T')[0], edad_meses: '',
    ce_absoluta: '', ce_relativa: null, calidad_seminal: '', motilidad: '', concentracion: '',
    libido: '', conformacion: '', apto_reproductor: null, veterinario: '', observaciones: '',
    adn_registro: false, adn_numero: '', adn_laboratorio: '', adn_fecha: '', adn_resultado: '',
  });

  const [ceRelativaCalculada, setCeRelativaCalculada] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'ce_absoluta' && value && macho) {
      const ceAbs = parseFloat(value);
      const edadMeses = calcularEdadMeses(macho.fecha_nacimiento);
      let pesoEstimado = edadMeses < 24 ? 300 + (edadMeses * 8) : 500;
      setCeRelativaCalculada(((ceAbs / pesoEstimado) * 100).toFixed(2));
    }
  };

  const buscarMachoPorRFID = async () => {
    if (!buscarIDE.trim()) return;
    setBuscandoMacho(true);
    setError('');
    try {
      const result = await buscarAnimalOffline(buscarIDE.trim(), user.establecimiento_id);
      if (result.animal.sexo !== 'macho') { setError('Este animal no es macho.'); return; }
      setMacho(result.animal);
      const edadMeses = calcularEdadMeses(result.animal.fecha_nacimiento);
      setFormData(prev => ({ ...prev, macho_id: result.animal.id, edad_meses: edadMeses }));
      setBuscarIDE('');
    } catch { setError('Animal no encontrado.'); setMacho(null); }
    finally { setBuscandoMacho(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);
    try {
      const payload = {
        ...formData,
        establecimiento_id: user.establecimiento_id,
        edad_meses: parseInt(formData.edad_meses),
        ce_absoluta: formData.ce_absoluta ? parseFloat(formData.ce_absoluta) : null,
        ce_relativa: ceRelativaCalculada ? parseFloat(ceRelativaCalculada) : null,
        motilidad: formData.motilidad ? parseFloat(formData.motilidad) : null,
        concentracion: formData.concentracion ? parseFloat(formData.concentracion) : null,
        libido: formData.libido ? parseInt(formData.libido) : null,
        conformacion: formData.conformacion ? parseInt(formData.conformacion) : null,
        apto_reproductor: formData.apto_reproductor === 'true' ? true : formData.apto_reproductor === 'false' ? false : null,
        adn_registro: formData.adn_registro || false,
        adn_numero: formData.adn_numero || null,
        adn_laboratorio: formData.adn_laboratorio || null,
        adn_fecha: formData.adn_fecha || null,
        adn_resultado: formData.adn_resultado || null,
      };
      Object.keys(payload).forEach(key => { if (payload[key] === '' || payload[key] === null) delete payload[key]; });

      const result = await submitOffline('/api/controles_machos/', payload);
      let msg = result.online ? 'Control registrado' : result.message;
      if (result.online && ceRelativaCalculada) msg += ` — CE relativa: ${ceRelativaCalculada} mm/kg`;
      setSuccessMessage(msg);

      setFormData({ macho_id: '', fecha: new Date().toISOString().split('T')[0], edad_meses: '', ce_absoluta: '', ce_relativa: null, calidad_seminal: '', motilidad: '', concentracion: '', libido: '', conformacion: '', apto_reproductor: null, veterinario: '', observaciones: '', adn_registro: false, adn_numero: '', adn_laboratorio: '', adn_fecha: '', adn_resultado: '' });
      setMacho(null); setCeRelativaCalculada(null);
      if (onSuccess) setTimeout(() => onSuccess(), 2000);
    } catch (err) { setError(err.response?.data?.detail || 'Error al registrar control'); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Control de Machos</h2>
        <div className="flex items-center space-x-2">
          {!navigator.onLine && <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-bold">MODO CAMPO</span>}
          {onCancel && (
            <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
              &times;
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {successMessage}
        </div>
      )}

      {/* Búsqueda de macho por RFID */}
      {!macho && (
        <div className="mb-6 p-4 bg-orange-50 border-2 border-orange-200 rounded-lg">
          <h3 className="text-sm font-semibold text-orange-800 mb-3">
            📡 Escanear Macho para Control
          </h3>
          <div className="flex space-x-2">
            <input
              type="text"
              value={buscarIDE}
              onChange={(e) => setBuscarIDE(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && buscarMachoPorRFID()}
              placeholder="Escanea RFID o ingresa IDE del macho..."
              className="flex-1 px-3 py-2 border border-orange-300 rounded-lg font-mono focus:ring-2 focus:ring-orange-500"
              autoFocus
            />
            <button
              onClick={buscarMachoPorRFID}
              disabled={buscandoMacho}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {buscandoMacho ? '...' : '🔍'}
            </button>
          </div>
        </div>
      )}

      {/* Información del macho */}
      {macho && (
        <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-semibold text-green-800 mb-2">
                ✅ Macho Seleccionado
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-medium">VID:</span> {macho.VID}
                </div>
                <div>
                  <span className="font-medium">IDE:</span> {macho.IDE || 'Sin IDE'}
                </div>
                <div>
                  <span className="font-medium">Edad:</span> {formData.edad_meses} meses
                </div>
                <div>
                  <span className="font-medium">Categoría:</span> {macho.categoria}
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                setMacho(null);
                setFormData(prev => ({ ...prev, macho_id: '', edad_meses: '' }));
              }}
              className="text-red-600 hover:text-red-800"
            >
              Cambiar
            </button>
          </div>
        </div>
      )}

      {/* Formulario de control */}
      {macho && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* SECCIÓN: DATOS GENERALES */}
          <div className="border-b pb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">📅 Datos Generales</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha del Control *
                </label>
                <input
                  type="date"
                  name="fecha"
                  value={formData.fecha}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Veterinario/Técnico
                </label>
                <input
                  type="text"
                  name="veterinario"
                  value={formData.veterinario}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Nombre del profesional"
                />
              </div>
            </div>
          </div>

          {/* SECCIÓN: CIRCUNFERENCIA ESCROTAL */}
          <div className="border-b pb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              📏 Circunferencia Escrotal (CE)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CE Absoluta (mm) *
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="ce_absoluta"
                  value={formData.ce_absoluta}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Ej: 320"
                  required
                />
              </div>

              {ceRelativaCalculada && (
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-800 mb-1">
                    ✅ CE Relativa (Automática)
                  </p>
                  <p className="text-2xl font-bold text-blue-600">
                    {ceRelativaCalculada} mm/kg
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    CE relativa = (CE / Peso adulto) × 100
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* SECCIÓN: ANDROLOGÍA */}
          <div className="border-b pb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              🔬 Andrología (14-15 meses)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Calidad Seminal
                </label>
                <select
                  name="calidad_seminal"
                  value={formData.calidad_seminal}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="">-- Seleccionar --</option>
                  <option value="excelente">Excelente</option>
                  <option value="buena">Buena</option>
                  <option value="regular">Regular</option>
                  <option value="mala">Mala</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motilidad (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  name="motilidad"
                  value={formData.motilidad}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="0-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Concentración (millones/ml)
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="concentracion"
                  value={formData.concentracion}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Ej: 800"
                />
              </div>
            </div>
          </div>

          {/* SECCIÓN: SCORES (1-10) */}
          <div className="border-b pb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              ⭐ Scores de Evaluación (1-10)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Libido (1-10)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  name="libido"
                  value={formData.libido}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="1-10"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Comportamiento sexual y capacidad de monta
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conformación (1-10)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  name="conformacion"
                  value={formData.conformacion}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="1-10"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Estructura física y características raciales
                </p>
              </div>
            </div>
          </div>

          {/* SECCIÓN: APTITUD */}
          <div className="border-b pb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              ✅ Aptitud Reproductiva
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ¿Apto para Reproducción?
              </label>
              <select
                name="apto_reproductor"
                value={formData.apto_reproductor === null ? '' : formData.apto_reproductor.toString()}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="">-- Seleccionar --</option>
                <option value="true">✅ Sí - Apto</option>
                <option value="false">❌ No - No Apto</option>
              </select>
            </div>
          </div>

          {/* SECCION: ADN (24 meses) */}
          {parseInt(formData.edad_meses) >= 20 && (
            <div className="border-b pb-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4" data-testid="adn-section-title">
                Registro de ADN (24 meses)
              </h3>
              <div className="mb-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="adn_registro"
                    checked={formData.adn_registro}
                    onChange={(e) => setFormData({ ...formData, adn_registro: e.target.checked })}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    data-testid="adn-registro-checkbox"
                  />
                  <span className="text-sm font-medium text-gray-700">ADN Registrado</span>
                </label>
              </div>
              {formData.adn_registro && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Numero de Registro ADN
                    </label>
                    <input
                      type="text"
                      name="adn_numero"
                      value={formData.adn_numero}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Ej: ADN-2026-00123"
                      data-testid="adn-numero-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Laboratorio
                    </label>
                    <input
                      type="text"
                      name="adn_laboratorio"
                      value={formData.adn_laboratorio}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Nombre del laboratorio"
                      data-testid="adn-laboratorio-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha Registro ADN
                    </label>
                    <input
                      type="date"
                      name="adn_fecha"
                      value={formData.adn_fecha}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      data-testid="adn-fecha-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Resultado / Observaciones ADN
                    </label>
                    <input
                      type="text"
                      name="adn_resultado"
                      value={formData.adn_resultado}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Resultado de la prueba ADN"
                      data-testid="adn-resultado-input"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Observaciones */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Observaciones
            </label>
            <textarea
              name="observaciones"
              value={formData.observaciones}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="Notas adicionales del control..."
            />
          </div>

          {/* Info del PDF */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 font-medium mb-2">
              Segun PDF - Evaluacion de Machos:
            </p>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>12 meses: Peso, altura, CE absoluta/relativa, scores (1-10)</li>
              <li>14-15 meses: Andrologia completa (calidad seminal, motilidad)</li>
              <li>24 meses: Libido, registro ADN, seleccion final</li>
              <li>Score compuesto: Suma de scores para ranking automatico</li>
            </ul>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Guardando...' : navigator.onLine ? 'Registrar Control' : 'Guardar Local'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default RegistrarControlMachoForm;
